/* @flow */

import * as React from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import moment from 'moment';
import styled from '@emotion/styled';
import { borderWidth } from 'polished';

import Button from 'app/components/commons/Button';
import Message from 'app/components/commons/Message';
import NavBarFlat from 'app/components/commons/NavBarFlat';
import SelectField from 'app/components/form/SelectField';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Modal from 'app/components/commons/Modal';
import { takeABreak, resetUserErrors, userSignOut } from 'app/actions/userActions';
import { hover } from 'app/utils/cssUtils';

type Props = {
  breakEndsAt?: string,
  onClearError: () => void,
  onSignout: () => void,
  onSubmit: () => void,
  inBreak?: boolean,
  requestingABreak: boolean,
  requestingABreakErrorMessage?: string,
  siteName: string
};

type State = {
  breakType: 'short' | 'long',
  confirming: boolean,
  period?: string
};

const TabItem = styled.div(
  ({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.color.basis2,
    borderColor: theme.color.basis2,
    borderStyle: 'solid',
    ...borderWidth(3, 0),
    color: theme.color.basis5,
    display: 'flex',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    fontSize: theme.fontSize.default,
    fontWeight: theme.fontWeight.demiBold,
    justifyContent: 'center',
    padding: '0.5em',
    textTransform: 'uppercase',
    transition: 'all 0.2s',
    ...hover({
      color: theme.color.white,
      cursor: 'pointer'
    })
  }),
  ({ active, theme }) =>
    active
      ? {
          borderBottomColor: theme.color.primary,
          color: theme.color.white
        }
      : {}
);

const Label = styled.div(({ theme }) => ({
  fontSize: theme.fontSize.large,
  marginBottom: theme.spacing.default
}));

const Text = styled.p(({ theme }) => ({
  fontSize: theme.fontSize.medium
}));

const ButtonContainer = styled.div({
  display: 'flex',
  justifyContent: 'center'
});

const shortPeriods = [...Array(31).keys()]
  .map(i => {
    const period = i > 0 ? `${i + 1} days` : `${i + 1} day`;
    return {
      label: period,
      value: period
    };
  })
  .concat(
    [...Array(7).keys()].map(i => {
      const period = i > 0 ? `${i + 1} weeks` : `${i + 1} week`;
      return {
        label: period,
        value: period
      };
    })
  );

const longPeriods = [
  {
    label: '6 months',
    value: '6 months'
  },
  {
    label: '1 year',
    value: '1 year'
  }
];

class AccountTakeABreak extends React.Component<Props, State> {
  props: Props;

  state: State = {
    breakType: 'short',
    confirming: false,
    period: undefined
  };

  static defaultProps = {
    breakEndsAt: undefined,
    inBreak: false,
    requestingABreakErrorMessage: undefined
  };

  componentDidUpdate(prevProps: Props) {
    const { onSignout, inBreak } = this.props;

    if (inBreak && !prevProps.inBreak) {
      onSignout();
    }
  }

  confirmSubmit = () => this.setState({ confirming: true });

  handleCancel = () => {
    const { onClearError } = this.props;
    this.setState({ confirming: false });
    onClearError();
  };

  handleSubmit = () => {
    const { onSubmit } = this.props;
    const { period } = this.state;

    onSubmit({ period });
  };

  handleSetPeriod = (value: string) => this.setState({ period: value });

  handleSelectBreakType = (breakType: string) =>
    this.setState({
      breakType,
      period: undefined
    });

  render() {
    const { breakEndsAt, requestingABreak, requestingABreakErrorMessage, siteName } = this.props;
    const { breakType, confirming, period } = this.state;
    const periods = breakType === 'short' ? shortPeriods : longPeriods;

    return (
      <div className="page-container">
        <Helmet title="Self Exclusion" />
        <h2>Take A Break</h2>
        <NavBarFlat>
          <TabItem
            active={breakType === 'short'}
            onClick={breakType === 'short' ? undefined : () => this.handleSelectBreakType('short')}
          >
            Short Break
          </TabItem>
          <TabItem
            active={breakType === 'long'}
            onClick={breakType === 'long' ? undefined : () => this.handleSelectBreakType('long')}
          >
            Close Account
          </TabItem>
        </NavBarFlat>
        {breakType === 'short' && (
          <div>
            <h3>
              If you take a short break from gambling at {siteName} this is what happens next:
            </h3>
            <ul>
              <li>
                <h3>All pending bets will stand.</h3>
              </li>
              <li>
                <h3>Account balance remains unchanged.</h3>
              </li>
              <li>
                <h3>You will be able to login to withdraw funds.</h3>
              </li>
              <li>
                <h3>
                  You will NOT be able to place a bet or enter a contest until the break is over.
                </h3>
              </li>
              <li>
                <h3>You will cease to receive marketing communications within 24 hours.</h3>
              </li>
            </ul>
          </div>
        )}
        {breakType === 'long' && (
          <div>
            <h3>If you take a long break from gambling at {siteName} this is what happens next:</h3>
            <ul>
              <li>
                <h3>All pending bets will stand.</h3>
              </li>
              <li>
                <h3>
                  ACCOUNT BALANCE REMAINS UNCHANGED. YOU CAN REQUEST A WITHDRAWAL BY CONTACTING
                  CUSTOMER SERVICES.
                </h3>
              </li>
              <li>
                <h3>You will cease to receive marketing communications within 24 hours.</h3>
              </li>
              <li>
                <h3>
                  You will need to contact Customer Services to re-activate your account after the
                  requested duration is over.
                </h3>
              </li>
            </ul>
          </div>
        )}
        {breakEndsAt && moment(breakEndsAt).isAfter(new Date()) ? (
          <Message kind="info">
            Your break ends at {moment(breakEndsAt).format('h:mm a on Do MMM YYYY')}
          </Message>
        ) : (
          <React.Fragment>
            <Label>How long do you want a {breakType} break for?</Label>
            <div>
              <SelectField
                defaultText="Select period"
                name="period"
                onChange={this.handleSetPeriod}
                options={periods}
                value={period}
                lightContent
                required
              />
            </div>
            <ButtonContainer>
              <Button
                block
                kind="danger"
                size="medium"
                disabled={!period}
                onClick={this.confirmSubmit}
                style={{ width: '200px' }}
              >
                {breakType === 'short' ? `Take ${period || 'a'} break` : `Close Account`}
              </Button>
            </ButtonContainer>
          </React.Fragment>
        )}
        {confirming && (
          <Modal isOpen={confirming} onCancel={this.handleCancel} size="small">
            <Text className="text-center">
              Are you sure you want to take {period || 'a'} break?
            </Text>
            {requestingABreakErrorMessage && (
              <FormValidationMessage message={requestingABreakErrorMessage} />
            )}
            <Button block kind="danger" disabled={requestingABreak} onClick={this.handleSubmit}>
              Take {period || 'a'} break
            </Button>
            <Button block kind="cancel" onClick={this.handleCancel}>
              Cancel
            </Button>
          </Modal>
        )}
      </div>
    );
  }
}

function mapStateToProps({ settings, user }) {
  return {
    breakEndsAt: user.currentUser.break_end_at,
    email: user.currentUser.email,
    inBreak: user.currentUser.inBreak,
    requestingABreak: user.requestingABreak,
    requestingABreakErrorMessage: user.requestingABreakErrorMessage,
    siteName: settings.siteName
  };
}

const actions = {
  onClearError: resetUserErrors,
  onSubmit: takeABreak,
  onSignout: userSignOut
};

export default connect(
  mapStateToProps,
  actions
)(AccountTakeABreak);
